import { defineStore } from 'pinia'
import {
  addToBasketApi,
  applyBasketPromoApi,
  fetchBasketApi,
  fetchBasketQuoteApi,
  removeFromBasketApi,
} from '@/entities/basket/api/basketApi'
import { fetchPublicDeliveryFee } from '@/entities/delivery/api/deliveryApi'
import { useDeliveryAddressStore } from '@/entities/delivery-address/model/useDeliveryAddressStore'
import { fetchRestaurantById } from '@/entities/restaurant/api/restaurantApi'
import { buildPayloadModifiers } from '@/features/cart/lib/buildPayloadModifiers'
import {
  isDeliveryQuoteError,
  shouldMarkOutsideDeliveryZone,
} from '@/features/cart/lib/resolveDeliveryQuoteErrorMessage'
import { buildAddToCartEvent, sendFacebookEvent } from '@/shared/lib/analytics'
import { useAuthStore } from '@/stores/auth'

const GUEST_COD_MAX_VND = 300_000

function applyDeliveryQuoteError(store, error, deliveryAddressStore) {
  store.quote = null
  if (isDeliveryQuoteError(error)) {
    store.quoteError = {
      code: String(error.code).trim(),
      message: typeof error.message === 'string' ? error.message : null,
    }
    if (shouldMarkOutsideDeliveryZone(error)) {
      deliveryAddressStore.markOutsideDeliveryZone()
    }
  } else {
    store.quoteError = null
  }
}

function normalizeQuantity(quantity) {
  const q = Number(quantity)
  return Number.isFinite(q) ? Math.max(1, Math.floor(q)) : 1
}

function findCartItem(items, lineKey) {
  const key = String(lineKey)
  return items.find((item) => String(item.lineKey || item.id) === key)
}

function buildDisplayModifiers(product, payloadModifiers) {
  if (!payloadModifiers || !product?.modifierGroups?.length) {
    return []
  }

  const result = []
  for (const group of product.modifierGroups) {
    const value = payloadModifiers[group.id]
    if (group.type === 'multiple') {
      const ids = Array.isArray(value) ? value : []
      for (const optionId of ids) {
        const option = group.options.find((item) => item.id === optionId)
        if (option) {
          result.push({
            groupId: group.id,
            groupTitle: group.title,
            optionId: option.id,
            title: option.title,
            priceDelta: option.priceDelta,
          })
        }
      }
      continue
    }

    if (!value) {
      continue
    }
    const option = group.options.find((item) => item.id === value)
    if (option) {
      result.push({
        groupId: group.id,
        groupTitle: group.title,
        optionId: option.id,
        title: option.title,
        priceDelta: option.priceDelta,
      })
    }
  }
  return result
}

function buildLineKey(productId, displayModifiers) {
  if (!displayModifiers.length) {
    return String(productId)
  }

  const optionsByGroup = new Map()
  for (const item of displayModifiers) {
    if (!item.groupId || !item.optionId) {
      continue
    }
    const optionIds = optionsByGroup.get(item.groupId) ?? []
    optionIds.push(item.optionId)
    optionsByGroup.set(item.groupId, optionIds)
  }

  const groupParts = [...optionsByGroup.entries()].map(([groupId, optionIds]) => {
    const sortedOptionIds = [...optionIds].sort()
    return `${groupId}:${sortedOptionIds.join(':')}`
  })

  return `${productId}:${groupParts.join('|')}`
}

function addItemLocally(items, product, quantity, payloadModifiers) {
  const safeQuantity = normalizeQuantity(quantity)
  const displayModifiers = buildDisplayModifiers(product, payloadModifiers)
  const lineKey = buildLineKey(product.id, displayModifiers)
  const existingItem = findCartItem(items, lineKey)
  if (existingItem) {
    existingItem.quantity += safeQuantity
    return
  }

  const basePrice = Number(product.price) || 0
  const priceDeltaSum = displayModifiers.reduce(
    (sum, item) => sum + (Number(item.priceDelta) || 0),
    0,
  )

  items.push({
    id: product.id,
    lineKey,
    title: product.title,
    price: basePrice + priceDeltaSum,
    basePrice,
    currency: product.currency || 'VND',
    weightGrams: product.weightGrams ?? null,
    imageUrl: product.imageUrl ?? null,
    compareAtPrice: product.compareAtPrice ?? null,
    quantity: safeQuantity,
    modifiers: displayModifiers,
    maxQuantity: null,
    available: true,
    unavailableMessage: null,
  })
}

export const useCartStore = defineStore('cart', {
  state: () => ({
    items: [],
    restaurantId: null,
    restaurantTitle: '',
    restaurantIsOpen: false,
    selectedPaymentMethodId: null,
    quote: null,
    quoteError: null,
    hasUnavailable: false,
    isSyncing: false,
    isPromoUpdating: false,
  }),
  getters: {
    totalCount: (state) => state.items.reduce((sum, item) => sum + item.quantity, 0),
    totalAmount: (state) => state.items.reduce((sum, item) => sum + item.price * item.quantity, 0),
    hasItems() {
      return this.totalCount > 0
    },
  },
  actions: {
    clear() {
      this.items = []
      this.restaurantId = null
      this.restaurantTitle = ''
      this.selectedPaymentMethodId = null
      this.restaurantIsOpen = false
      this.quote = null
      this.quoteError = null
      this.hasUnavailable = false
      this.isPromoUpdating = false
    },

    async clearItems() {
      const authStore = useAuthStore()
      if (!authStore.isLoggedIn) {
        this.clear()
        return
      }

      const snapshot = [...this.items]
      for (const item of snapshot) {
        await removeFromBasketApi({
          productId: item.id,
          lineKey: item.lineKey,
        })
      }
      await this.syncFromServer()
    },

    setSelectedPaymentMethod(paymentMethodId) {
      this.selectedPaymentMethodId = paymentMethodId ? String(paymentMethodId) : null
    },

    rebuildGuestQuoteTotals() {
      if (!this.quote?.isEstimate || this.quoteError) {
        return
      }
      const itemsTotalVnd = this.totalAmount
      const deliveryFeeVnd = Number(this.quote.deliveryFeeVnd) || 0
      const amountDueVnd = Math.max(0, itemsTotalVnd + deliveryFeeVnd)
      this.quote = {
        ...this.quote,
        itemsCount: this.totalCount,
        itemsTotalVnd,
        amountDueVnd,
        cashOnDeliveryAllowed: amountDueVnd <= GUEST_COD_MAX_VND,
      }
    },

    async refreshGuestDeliveryFee(location) {
      const deliveryAddressStore = useDeliveryAddressStore()
      try {
        const fee = await fetchPublicDeliveryFee({
          restoId: this.restaurantId,
          lat: location.lat,
          lng: location.lng,
        })
        const itemsTotalVnd = this.totalAmount
        const deliveryFeeVnd = fee.deliveryFeeVnd
        const amountDueVnd = Math.max(0, itemsTotalVnd + deliveryFeeVnd)
        this.quote = {
          itemsCount: this.totalCount,
          itemsTotalVnd,
          deliveryFeeVnd,
          amountDueVnd,
          amountRub: null,
          cashOnDeliveryAllowed: amountDueVnd <= GUEST_COD_MAX_VND,
          zone: fee.zone,
          promoCode: null,
          discountVnd: 0,
          promoError: null,
          isEstimate: true,
        }
        this.quoteError = null
        return this.quote
      } catch (error) {
        applyDeliveryQuoteError(this, error, deliveryAddressStore)
        return null
      }
    },

    async refreshQuote() {
      if (!this.items.length) {
        this.quote = null
        this.quoteError = null
        return null
      }

      const deliveryAddressStore = useDeliveryAddressStore()
      const location = deliveryAddressStore.normalizeLocation(deliveryAddressStore.location)

      if (this.restaurantId && !location) {
        this.quote = null
        this.quoteError = { code: 'delivery_no_location', message: null }
        return null
      }

      const authStore = useAuthStore()
      if (!authStore.isLoggedIn) {
        if (!this.restaurantId || !location) {
          this.quote = null
          this.quoteError = null
          return null
        }
        return this.refreshGuestDeliveryFee(location)
      }

      try {
        this.quote = await fetchBasketQuoteApi(this.selectedPaymentMethodId, location)
        this.quoteError = null
        if (this.quote?.promoError && this.quote?.promoCode) {
          try {
            await applyBasketPromoApi(null)
            this.quote = await fetchBasketQuoteApi(this.selectedPaymentMethodId, location)
          } catch {}
        }
        return this.quote
      } catch (error) {
        applyDeliveryQuoteError(this, error, deliveryAddressStore)
        return null
      }
    },

    async applyPromo(code) {
      const authStore = useAuthStore()
      if (!authStore.isLoggedIn || !this.items.length) {
        return null
      }

      const normalized =
        code == null || String(code).trim() === '' ? null : String(code).trim()
      const deliveryAddressStore = useDeliveryAddressStore()
      const location = deliveryAddressStore.normalizeLocation(deliveryAddressStore.location)

      if (normalized && !location) {
        const error = new Error('')
        error.code = 'delivery_no_location'
        throw error
      }

      this.isPromoUpdating = true
      try {
        await applyBasketPromoApi(normalized, location)
        return await this.refreshQuote()
      } finally {
        this.isPromoUpdating = false
      }
    },

    async clearPromo() {
      return this.applyPromo(null)
    },

    async loadRestaurantTitle() {
      if (!this.restaurantId) {
        this.restaurantTitle = ''
        return
      }
      const restaurant = await fetchRestaurantById(this.restaurantId)
      this.restaurantTitle = restaurant?.title ?? ''
      this.restaurantIsOpen = restaurant?.isOpen ?? false
    },

    async syncFromServer() {
      const authStore = useAuthStore()
      if (!authStore.isLoggedIn) {
        await this.refreshQuote()
        return
      }

      this.isSyncing = true
      try {
        const { items, restaurantId, hasUnavailable } = await fetchBasketApi()
        this.items = items
        this.restaurantId = restaurantId
        this.hasUnavailable = hasUnavailable
        await this.loadRestaurantTitle()
        await this.refreshQuote()
      } finally {
        this.isSyncing = false
      }
    },

    async syncAfterAuth() {
      const authStore = useAuthStore()
      if (!authStore.isLoggedIn) {
        return
      }

      const guestItems = this.items.length ? [...this.items] : []
      if (!guestItems.length) {
        await this.syncFromServer()
        return
      }

      this.isSyncing = true
      try {
        const { items } = await fetchBasketApi()
        for (const item of items) {
          await removeFromBasketApi({
            productId: item.id,
            lineKey: item.lineKey,
          })
        }
        for (const item of guestItems) {
          await addToBasketApi({
            productId: item.id,
            quantity: item.quantity,
            modifiers: buildPayloadModifiers(item.modifiers),
          })
        }
      } finally {
        this.isSyncing = false
      }

      await this.syncFromServer()
    },

    async addItem(product, quantity = 1, modifiers, analytics = {}) {
      const authStore = useAuthStore()
      const safeQuantity = normalizeQuantity(quantity)
      const payloadModifiers =
        modifiers && Object.keys(modifiers).length ? modifiers : undefined

      if (authStore.isLoggedIn) {
        await addToBasketApi({
          productId: product.id,
          quantity: safeQuantity,
          modifiers: payloadModifiers,
        })
        await this.syncFromServer()
        sendFacebookEvent('AddToCart', buildAddToCartEvent(product, safeQuantity, analytics.source))
        return
      }

      addItemLocally(this.items, product, safeQuantity, payloadModifiers)
      if (product.restaurantId) {
        this.restaurantId = String(product.restaurantId)
        await this.loadRestaurantTitle()
      }
      await this.refreshQuote()
      sendFacebookEvent('AddToCart', buildAddToCartEvent(product, safeQuantity, analytics.source))
    },

    async setItemQuantity(lineKey, quantity) {
      const item = findCartItem(this.items, lineKey)
      if (!item) {
        return
      }

      const q = Math.floor(Number(quantity))
      const authStore = useAuthStore()
      const payloadModifiers = buildPayloadModifiers(item.modifiers)

      if (authStore.isLoggedIn) {
        if (!Number.isFinite(q) || q < 1) {
          await removeFromBasketApi({
            productId: item.id,
            lineKey: item.lineKey,
          })
        } else {
          const delta = q - item.quantity
          if (delta > 0) {
            await addToBasketApi({
              productId: item.id,
              quantity: delta,
              modifiers: payloadModifiers,
            })
          } else if (delta < 0) {
            await removeFromBasketApi({
              productId: item.id,
              quantity: -delta,
              lineKey: item.lineKey,
            })
          }
        }
        await this.syncFromServer()
        return
      }

      if (!Number.isFinite(q) || q < 1) {
        this.removeItemLocal(lineKey)
        await this.refreshQuote()
        return
      }
      item.quantity = q
      this.rebuildGuestQuoteTotals()
    },

    removeItemLocal(lineKey) {
      const key = String(lineKey)
      const idx = this.items.findIndex((item) => String(item.lineKey || item.id) === key)
      if (idx !== -1) {
        this.items.splice(idx, 1)
      }
      if (!this.items.length) {
        this.restaurantId = null
        this.restaurantTitle = ''
        this.restaurantIsOpen = false
        this.quote = null
        this.quoteError = null
      }
    },

    async removeItem(lineKey) {
      const item = findCartItem(this.items, lineKey)
      const authStore = useAuthStore()

      if (authStore.isLoggedIn) {
        await removeFromBasketApi({
          productId: item?.id ?? lineKey,
          lineKey: item?.lineKey ?? lineKey,
        })
        await this.syncFromServer()
        return
      }

      this.removeItemLocal(lineKey)
      if (this.items.length) {
        this.rebuildGuestQuoteTotals()
      }
    },
  },
})
